import { useState } from "react";
import { Minus, Plus, Trash2, Crown, Layers } from "lucide-react";
import { assetUrl } from "@/lib/api";
import { ZONES } from "@/lib/decks";
import { ManaCost } from "@/components/common/ManaCost";
import { IssueBadge } from "@/components/decks/LegalityBadge";

const CORE = ["commander", "main", "sideboard", "tokens"];

function EntryRow({ entry, index, onChange, readOnly, zones, backs, issues = [] }) {
  const [showBack, setShowBack] = useState(false);
  const card = entry.card;
  const id = `deck-entry-${entry.zone}-${index}`;
  return (
    <li className="group rounded-md px-2 py-1.5 hover:bg-white/[0.03]" data-testid={id}>
    <div className="flex flex-wrap items-center gap-2">
      <span className="w-7 text-right font-mono text-sm text-aether-300" data-testid={`${id}-qty`}>{entry.quantity}×</span>
      {card?.image_url ? <img src={assetUrl(card.image_url)} alt="" className="h-9 w-7 rounded object-cover" loading="lazy" /> : <div className="h-9 w-7 rounded bg-red-950/50" />}
      <div className="min-w-[8rem] flex-1">
        <p className="truncate text-sm text-slate-100">{card?.name || "Unavailable card"}</p>
        <p className="truncate text-[11px] text-slate-500">
          <span className={`mr-1 font-mono uppercase ${entry.source === "official" ? "text-aether-400" : "text-copper-300"}`}>{entry.source === "official" ? "OFF" : "CUS"}</span>
          {card?.type_line}
        </p>
        {issues.length > 0 && <div className="mt-0.5 flex flex-wrap gap-1" data-testid={`${id}-issues`}>{issues.map((x) => <IssueBadge key={x.code} issue={x} testid={`${id}-issue-${x.code}`} />)}</div>}
      </div>
      <ManaCost cost={card?.mana_cost} />
      {!readOnly && (
        <div className="ml-auto flex items-center opacity-80 group-hover:opacity-100">
          <button className="btn-ghost px-1.5" onClick={() => onChange({ quantity: entry.quantity - 1 })} aria-label="Remove one copy" data-testid={`${id}-dec`}><Minus className="h-3.5 w-3.5" /></button>
          <button className="btn-ghost px-1.5" onClick={() => onChange({ quantity: entry.quantity + 1 })} aria-label="Add one copy" data-testid={`${id}-inc`}><Plus className="h-3.5 w-3.5" /></button>
          {entry.zone !== "commander" && (
            <button className="btn-ghost px-1.5" onClick={() => onChange({ zone: "commander", quantity: 1 })} aria-label="Set as commander" title="Set as commander" data-testid={`${id}-commander`}><Crown className="h-3.5 w-3.5" /></button>
          )}
          <button className={`btn-ghost px-1.5 ${entry.card_back_id ? "text-aether-300" : ""}`} onClick={() => setShowBack(!showBack)} aria-label="Card back override" title="Card back override" data-testid={`${id}-back-toggle`}><Layers className="h-3.5 w-3.5" /></button>
          <select className="field w-24 px-1 py-1 text-[11px]" value={entry.zone} onChange={(e) => onChange({ zone: e.target.value })} aria-label="Move to zone" data-testid={`${id}-zone`}>
            {(zones || ZONES).map((z) => <option key={z.key} value={z.key}>{z.label}</option>)}
          </select>
          <button className="btn-ghost px-1.5 hover:text-red-300" onClick={() => onChange({ quantity: 0 })} aria-label="Remove card" data-testid={`${id}-remove`}><Trash2 className="h-3.5 w-3.5" /></button>
        </div>
      )}
    </div>
      {!readOnly && showBack && (
        <label className="mt-1 flex items-center justify-end gap-2 text-[11px] text-slate-400">Back for this card
          <select className="field w-44 py-1 text-[11px]" value={entry.card_back_id || ""} onChange={(e) => onChange({ card_back_id: e.target.value || null })} data-testid={`${id}-back-select`}>
            <option value="">Inherit (zone / card / set / deck)</option>
            {(backs || []).map((b) => <option key={b.card_back_id} value={b.card_back_id}>{b.name}</option>)}
          </select>
        </label>
      )}
    </li>
  );
}

export function DeckZones({ entries, onChange, readOnly = false, zones = ZONES, pinned = [], backs = [], issues = {} }) {
  return (
    <div className="space-y-5" data-testid="deck-zones">
      {zones.map((z) => {
        const list = entries.map((e, i) => ({ e, i })).filter(({ e }) => e.zone === z.key);
        const count = list.reduce((s, { e }) => s + e.quantity, 0);
        if ((readOnly || (!CORE.includes(z.key) && !pinned.includes(z.key))) && !count) return null;
        return (
          <section key={z.key} className="panel p-4" data-testid={`deck-zone-${z.key}`}>
            <h3 className="mb-2 flex items-center justify-between font-heading text-sm text-amber-200">
              {z.label}<span className="font-mono text-xs text-slate-500" data-testid={`deck-zone-${z.key}-count`}>{count}</span>
            </h3>
            {list.length ? (
              <ul>{list.map(({ e, i }, n) => <EntryRow key={`${e.source}-${e.ref_id}-${e.zone}`} entry={e} index={n} readOnly={readOnly} zones={zones} backs={backs} issues={issues[`${e.zone}|${e.source}|${e.ref_id}`]} onChange={(patch) => onChange(i, patch)} />)}</ul>
            ) : <p className="text-xs text-slate-600">Empty</p>}
          </section>
        );
      })}
    </div>
  );
}
